import { useEffect, useRef, useState, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, size, replace, cloneDeep, isNull, isUndefined } from "lodash";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import inventoryTxnService from "@plannel/services/data/inventory-txn-service"
import locationService from "@plannel/services/data/location-service"
import customerService from "@plannel/services/data/customer-service"
import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridDatePicker from "@plannel/components/GridDatePicker"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import numberUtil from '@plannel/utils/number-util';
import CodeUtil from "@plannel/pages/utils/code-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const InventoryTransactionMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxNotifyState = reduxUtil.getViewState(`${viewName}_NOTIFY`);
  const reduxDispatch = useDispatch();
  const showLimitSnackbar = useRef(reduxNotifyState ? reduxNotifyState?.showLimitSnackbar : true);

  const pageSizes = [100, 200, 500];
  const [resultTotalCount, setResultTotalCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  // v26.10: 거래처 검색을 숨긴 동안엔 예전에 저장된 검색값도 읽지 않는다 — 안 보이는 조건이 조회에 걸리면 안 된다
  const customerSearch = useRef(CodeUtil.SHOW_CHANNEL ? (reduxViewState?.customerSearch ?? "") : "");
  const extraCustomerCodesRef = useRef(CodeUtil.SHOW_CHANNEL ? (reduxViewState?.extraCustomerCodes ?? []) : []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [limitInfo, setLimitInfo] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [inventoryTxns, setInventoryTxns] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  // 초기값은 반드시 null 이어야 한다. undefined 로 두면 DatePicker 가 uncontrolled 로 시작했다가
  // 최근 거래일자 응답이 오는 순간 controlled 로 바뀌어 MUI 가 콘솔 에러를 낸다.
  const [date, setDate] = useState(reduxViewState?.date ?? null);

  const currencyMap = useRef({});
  const [csvColDef, setCsvColDef] = useState([]);
  const userData = JSON.parse(localStorage.getItem("user"));
  const EXCEL_EXPORT = "excelExport";

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (!isEmpty(locationSearch)) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (date) {
      searchFilters.date = date;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveInventoryTxn = () => {
    const params = getRequestParams();
    inventoryTxnService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems, isLimit, limitSize, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setLimitInfo({ isLimit, limitSize });
        }

        setInventoryTxns(results);
        setTotalPages(totalPages);
        setResultTotalCount(totalItems);
        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { date, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, locationSearch, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const retrieveRecentDate = () => {
    inventoryTxnService.getRecentTxnDate()
    .then(response => {
      if (response.data) 
      {
        setDate(response.data)
      } else {
        setDate(new ZDate())
      }
    })
    .catch(e => {
      debug(e);
    });
  }

  const retrieveLocation = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        setLocations(response.data.results);
      })
      .catch(e => {
        debug(e);
      })
  }

  const retrieveCustomers = () => {
    customerService.getLookup()
      .then(response => {
        setCustomers(response.data.results);
      })
      .catch(e => {
        debug(e);
      })
  }

  const locationCdValueFormatter = (location) => {
    const formatter = location?.value?.locCd;
    return formatter ?? "";
  }

  const locationNmValueFormatter = (location) => {
    const formatter = location?.value?.name;
    return formatter ?? "";
  }
  
  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.location?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      } 
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = numberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;


  const columnDefs = [
    {
      headerName: "locCd", field: "location", colId: "locCd",
      type: ['nonEditableColumn'],
      cellClass: 'stringType',
      valueFormatter: locationCdValueFormatter,
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "locCd", isDescending),
      width: 150, filterType: "string"
    },
    {
      headerName: "locName", field: "location", colId: "locNm",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: locationNmValueFormatter,
      cellEditorParams: {
        values: locations
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 200, 
      filterType: "string",
    },
    { headerName: "itemCd", field: "itemCd", width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "itemNm", type: ["nonEditableColumn"], width: 300, filterType: "string" },
    /* customer 는 item 과 달리 nullable — 이름에서 빈 값을 고르면 거래처 구분 없는 재고로 저장된다
       v26.10: Channel Planning 미출시 — 두 열을 내린다(SHOW_CHANNEL). 행 데이터엔 값이 남아 저장해도 보존된다 */
    ...(CodeUtil.SHOW_CHANNEL ? [
    { headerName: "customerCd", field: "customerCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType', filterType: "string" },
    {
      headerName: "customerName", field: "customerNm",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: {
        values: customers.map(customer => customer.name)
      },
      valueSetter: (params) => {
        const customer = customers.find(c => c.name === params.newValue);
        params.data.customerNm = customer ? customer.name : null;
        params.data.customerCd = customer ? customer.customerCd : null;
        return true;
      },
      width: 300, filterType: "string"
    },
    ] : []),
    { headerName: "invCd", field: "invCd", width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "txnDate", field: "txnDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "expDate", field: "expDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "invQty", field: "invQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "invAmt", field: "invAmt", type: ["numberColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 120, filterType: "number" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const insertRow = () => {
    const newRow = {
      id: null,
      location: null,
      itemCd: null,
      itemNm: null,
      customerCd: null,
      customerNm: null,
      txnDate: null,
      invQty: null,
      invAmt: null,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeInventoryTxn = () => {
    const inventoryTxnIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        inventoryTxnIds.push(data.id)
      }
    );

    if (inventoryTxnIds.length > 0) {
      inventoryTxnService.remove(inventoryTxnIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveInventoryTxn();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveInventoryTxn = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      inventoryTxnService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveInventoryTxn();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: "MSG_NoSaveData" });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveInventoryTxn();
    } else if (action === "DELETE") {
      removeInventoryTxn();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveInventoryTxn();
    }
  }

  const onGridReady = (params) => {
    retrieveLocation();
    retrieveCustomers();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    setCsvColDef(cloneDeep(columnDefs))
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (csvColDef.isEmpty) {
      return;
    }
    for (const col of csvColDef) {
      if (col.valueFormatter?.customType === EXCEL_EXPORT) {
        col.cellRenderer = null;
        col.valueGetter = params => currencyRenderer(params, true);
        col.cellClass = params => {
          const value = Number(params.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvColDef])

  useEffect(() => {
    if (!reduxViewState?.date) {
      retrieveRecentDate();
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  useEffect(() => {
    if (gridLoading && date) {
      DataState.initialize(gridRef.current?.api);
      retrieveInventoryTxn();
    }

    return () => {
      setInventoryTxns([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading && date) {
      initPageToRetrieve();
    }

    return () => {
      setInventoryTxns([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, locationSearch, date]);

  useEffect(() => {
    if (limitInfo.isLimit && showLimitSnackbar.current) {
      setSnackInfo({ open: true, severity: "info", content: `${t("MSG_LimitedSearchResults", { N: limitInfo.limitSize })}` });
      reduxDispatch(reduxUtil.updateViewState({viewName : `${viewName}_NOTIFY`, showLimitSnackbar: false}));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitInfo.isLimit, showLimitSnackbar.current]);

  useEffect(() => {
    // 드롭다운 목록이 늦게 도착하므로 도착 후 columnDefs(cellEditorParams.values)를 다시 반영한다
    if (!isEmpty(locations) || !isEmpty(customers)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, customers]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <Box sx={{ display: "flex", mr: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t("periodStart")}
              value={date}
              // maxDate={phaseMaxDate}
              // minDate={phaseMinDate}
              format={"yyyy-MM-dd"}
              views={["year", "month", "day"]}
              openTo={"day"}
              onAccept={newStartValue => {
                setDate(newStartValue);
                reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, date }));
              }}
              slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
            />
          </LocalizationProvider>
        </Box>
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 190, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        {CodeUtil.SHOW_CHANNEL && (
          <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} useAccessFilter={false} extraCodesRef={extraCustomerCodesRef} />
        )}
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={inventoryTxnService} columnDefs={csvColDef} totalRows={resultTotalCount}
            params={getRequestParams()} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="inventory-transaction-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={inventoryTxns}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              statusBar={statusBar}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        disableButton={dialogInfo.disableButton}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(InventoryTransactionMaster);